import './testimonial.scss';
import Swiper from 'swiper';
import { A11y, Navigation, Keyboard } from 'swiper/modules';

class Testimonial {
    static #ATTRIBUTES = {
        init: 'data-testimonial',
    };

    #root;
    $$slides;
    sliderActive;
    $progressbar;
    $prevButton;
    $nextButton;

    constructor(element) {
        this.#root = element;
        this.#queryElements();
    }

    #queryElements () {
        const { init } = Testimonial.#ATTRIBUTES;
        this.$slider = this.#root.querySelector(`[${init}="slider"]`);
        this.$$slides = this.#root.querySelectorAll(`[${init}="slide"]`);
    }

    initialize () {
        this.#initSlider();
    }

    /**
     * PRIVATE method to initialize the slider.
     */
    #initSlider () {
        this.sliderActive = true;
        this.$progressbar = this.$slider.querySelector('.swiper-pagination-progressbar-fill');

        const updateProgressbar = (index) => {
            const progress = 1 / this.$$slides.length * (index + 1);
            if (this.$progressbar) {
                this.$progressbar.style.transform = 'scaleX(' + progress + ')';
                this.$progressbar.setAttribute('aria-value', Math.round(progress * 100));
            }
        };

        this.$prevButton = this.#root.querySelector('.swiper-button-prev');
        this.$nextButton = this.#root.querySelector('.swiper-button-next');
        this.$fraction = this.#root.querySelector('.swiper-pagination-fraction');

        this.slider = new Swiper(this.$slider, {
            modules: [A11y, Navigation, Keyboard],
            a11y: true,
            slidesPerView: 'auto',
            lazyPreloadPrevNext: true,
            slideToClickedSlide: false,
            speed: 500,
            navigation: {
                nextEl: this.$nextButton,
                prevEl: this.$prevButton
            },
            keyboard: true,
            on: {
                init: (swiper) => {
                    updateProgressbar(swiper.activeIndex);
                    this.#improveAccessibility(swiper);
                    this.#progressMutationObserver();
                },
                slideChange: (swiper) => {
                    this.#improveAccessibility(swiper);
                },
                slideChangeTransitionStart: (swiper) => {
                    updateProgressbar(swiper.activeIndex);
                },
                lock: (swiper) => {
                    if (swiper.isLocked) {
                        this.$slider.classList.add('swiper-locked');
                    }
                },
                unlock: (swiper) => {
                    if (!swiper.isLocked) {
                        this.$slider.classList.remove('swiper-locked');
                    }
                }
            }
        });
    }

    /**
     * PRIVATE method to improve the Swiper Accessibility.
     */
    #improveAccessibility (swiper) {
        swiper.slides.forEach(($slide, index) => {
            const slideIsActive = index === swiper.activeIndex;
            $slide.setAttribute('aria-hidden', !slideIsActive);
            slideIsActive ? $slide.removeAttribute('disabled') : $slide.setAttribute('disabled', '');
        });
    }

    #progressMutationObserver () {
        if (this.$progressbar) {
            const observer = new MutationObserver((mutationsList) => {
                for (const mutation of mutationsList) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'aria-value') {
                        const targetElement = mutation.target;
                        const currentValue = targetElement.getAttribute('aria-value');

                        if (currentValue !== null) {
                            targetElement.setAttribute('aria-valuenow', currentValue);
                            targetElement.removeAttribute('aria-value'); // Entferne das falsche Attribut
                        }
                    }
                }
            });

            observer.observe(this.$progressbar, {
                attributes: true
            });

            // Perform an initial correction if the error already exists at startup
            if (this.$progressbar.hasAttribute('aria-value')) {
                this.$progressbar.setAttribute('aria-valuenow', this.$progressbar.getAttribute('aria-value'));
                this.$progressbar.removeAttribute('aria-value');
                this.$progressbar.setAttribute('aria-valuemin', '0');
                this.$progressbar.setAttribute('aria-valuemax', '100');
            }
        }
    }
}

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-testimonial="root"]');
        $$roots.forEach($root => {
            if ($root.sliderAPI) return; // Already initialized?
            $root.sliderAPI = new Testimonial($root);
            $root.sliderAPI.initialize();
        });
    }
});

export { Testimonial };


